// src/App.js
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import './App.css';
import MaintenancePage from './components/MaintenancePage';
import axios from 'axios';

export default function App(){
  const [openIds, setOpenIds] = useState(() => new Set());
  const [leadForm, setLeadForm] = useState({ fullName: '', email: '', phone: '' });
  const [leadStatus, setLeadStatus] = useState('idle');
  const [leadMessage, setLeadMessage] = useState('');

  // ====== Maintenance ======
  const isMaintenanceMode = false;
  const [isIpAllowed, setIsIpAllowed] = useState(false);

  useEffect(() => {
    const checkIpRoute = async () => {
      try {
        const response = await axios.get('/api/ip/check-ip');
        if (response.data && response.data.allowed === true) setIsIpAllowed(true);
        else setIsIpAllowed(false);
      } catch (error) {
        console.error('Error checking IP on server:', error);
        setIsIpAllowed(false);
      }
    };

    checkIpRoute();
  }, []);

  const isMaintenanceActive = isMaintenanceMode && !isIpAllowed;

  // ====== Contacts ======
  const emailUrl = 'mailto:mozartovich@gmail.com';
  const waUrl = 'https://wa.me/+380984204236';

  // ====== Cover ======
  const coverUrl = '/gallery/1.jpg';

  // ====== Lightbox ======
  const [lightboxSrc, setLightboxSrc] = useState('');
  const [lightboxAlt, setLightboxAlt] = useState('');
  const [lbOpen, setLbOpen] = useState(false);

  const closeLb = useCallback(() => {
    setLbOpen(false);
    setTimeout(() => {
      setLightboxSrc('');
      setLightboxAlt('');
    }, 220);
  }, []);

  useEffect(() => {
    if (!lbOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeLb();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [lbOpen, closeLb]);

  useEffect(() => {
    if (!lbOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [lbOpen]);

  const openLb = useCallback((src, alt='') => {
    setLightboxSrc(src);
    setLightboxAlt(alt);
    setLbOpen(true);
  }, []);

  // ====== Tracking ======
  const sendEvent = useCallback(async (eventName, userData = {}) => {
    const payload = JSON.stringify({ eventName, userData });

    try{
      if (navigator.sendBeacon){
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon('/api/track-event', blob);
        return;
      }
    }catch{}

    try{
      await fetch('/api/track-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      });
    }catch{}
  }, []);

  const fire = useCallback(async (name) => {
    try { await sendEvent(name); } catch {}
  }, [sendEvent]);

  const onEmailClick = useCallback(() => {
    fire('OSINT Email Tunnel');
  }, [fire]);

  const onWhatsAppClick = useCallback(() => {
    fire('OSINT WhatsApp Tunnel');
  }, [fire]);

  const onLeadChange = useCallback((e) => {
    const { name, value } = e.target;
    setLeadForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const submitLead = useCallback(async (e) => {
    e.preventDefault();

    const payload = {
      fullName: leadForm.fullName.trim(),
      email: leadForm.email.trim(),
      phone: leadForm.phone.trim(),
      source: 'osint-landing',
    };

    if (!payload.fullName || !payload.email || !payload.phone) {
      setLeadStatus('error');
      setLeadMessage('Будь ласка, заповніть ім’я, email та телефон.');
      return;
    }

    try {
      setLeadStatus('loading');
      setLeadMessage('');

      await axios.post('/api/leads', payload);
      await sendEvent('OSINT Lead Form Submit', { email: payload.email, phone: payload.phone });

      setLeadStatus('success');
      setLeadMessage('Дякуємо. Запит отримано, з вами зв’яжуться найближчим часом.');
      setLeadForm({ fullName: '', email: '', phone: '' });
    } catch (error) {
      console.error('Lead submit error:', error);
      setLeadStatus('error');
      setLeadMessage(error.response?.data?.error || 'Сталася помилка. Спробуйте ще раз.');
    }
  }, [leadForm, sendEvent]);

  // ====== Accordions ======
  const toggle = (id) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sections = useMemo(() => ([
    {
      id: 'services',
      title: 'Послуги',
      meta: 'Аналітика • OSINT • Розслідування • Траблшутинг',
      body: (
        <>
          <div className="mediaCard">
            <button
              type="button"
              className="mediaCard mediaCardBtn"
              onClick={() => openLb('/gallery/services.jpg', 'Аналітика, OSINT, розслідування та траблшутинг')}
              aria-label="Відкрити фото послуг"
            >
              <div className="mediaFrame mediaFrame11" aria-label="Фото послуг">
                <img
                  className="mediaImg"
                  src="/gallery/services.jpg"
                  alt="Аналітика, OSINT, розслідування та траблшутинг"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const phEl = e.currentTarget.parentElement?.querySelector('.mediaPlaceholder');
                    if (phEl) phEl.style.display = 'grid';
                  }}
                />
                <div className="mediaPlaceholder" style={{ display: 'none' }}>
                  <div className="placeholderText">Немає зображення</div>
                </div>
                <div className="tapHint" aria-hidden="true">Натисніть • повний екран</div>
              </div>
            </button>
          </div>

          <div className="textBlock">
            <p>
              Допомагаю розібратись у заплутаних ситуаціях, де багато суперечливої інформації, сумнівів або незрозумілих обставин.
            </p>
            <p>
              Працюю виключно в межах законодавства та з відкритими джерелами інформації. Основний підхід — конфіденційність, системність та уважна перевірка фактів.
            </p>
          </div>

          <div className="serviceList">
            <div className="serviceItem">
              <div className="serviceTitle">Аналітика</div>
              <div className="serviceText">
                Аналіз ситуацій, фактів, ризиків та великого обсягу інформації з підготовкою структурованих висновків.
              </div>
            </div>

            <div className="serviceItem">
              <div className="serviceTitle">OSINT</div>
              <div className="serviceText">
                Перевірка відкритої інформації, пошук зв’язків, виявлення логічних невідповідностей та формування цілісної картини.
              </div>
            </div>

            <div className="serviceItem">
              <div className="serviceTitle">Розслідування</div>
              <div className="serviceText">
                Приватні та корпоративні розслідування в межах законодавства, з фокусом на фактах, ризиках і прихованих зв’язках.
              </div>
            </div>

            <div className="serviceItem">
              <div className="serviceTitle">Траблшутинг</div>
              <div className="serviceText">
                Незалежний аналітичний погляд на проблему, відокремлення фактів від емоцій та пошук найбільш ефективного рішення.
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      id: 'troubleshooting',
      title: 'Траблшутинг',
      meta: 'Факти • ризики • логіка • висновки',
      body: (
        <>
          <div className="mediaCard">
            <button
              type="button"
              className="mediaCard mediaCardBtn"
              onClick={() => openLb('/gallery/troubleshooting.jpg', 'Траблшутинг та аналітична робота')}
              aria-label="Відкрити фото траблшутингу"
            >
              <div className="mediaFrame mediaFrame11" aria-label="Фото траблшутингу">
                <img
                  className="mediaImg"
                  src="/gallery/troubleshooting.jpg"
                  alt="Траблшутинг та аналітична робота"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const phEl = e.currentTarget.parentElement?.querySelector('.mediaPlaceholder');
                    if (phEl) phEl.style.display = 'grid';
                  }}
                />
                <div className="mediaPlaceholder" style={{ display: 'none' }}>
                  <div className="placeholderText">Немає зображення</div>
                </div>
                <div className="tapHint" aria-hidden="true">Натисніть • повний екран</div>
              </div>
            </button>
          </div>

          <div className="textBlock">
            <p>
              Траблшутинг потрібен тоді, коли ситуація виглядає заплутаною, інформація суперечить сама собі, а рішення неможливо прийняти без системного аналізу.
            </p>
            <p>
              Моя задача — розкласти проблему на факти, перевірити логіку, знайти слабкі місця та підготувати зрозумілий аналітичний висновок.
            </p>
          </div>

          <div className="serviceList">
            <div className="serviceItem"><div className="serviceTitle">Аналіз ситуацій та фактів</div></div>
            <div className="serviceItem"><div className="serviceTitle">Перевірка відкритої інформації</div></div>
            <div className="serviceItem"><div className="serviceTitle">Виявлення логічних невідповідностей</div></div>
            <div className="serviceItem"><div className="serviceTitle">Аналіз ризиків</div></div>
            <div className="serviceItem"><div className="serviceTitle">Структуризація великого обсягу інформації</div></div>
            <div className="serviceItem"><div className="serviceTitle">Незалежний аналітичний погляд на проблему</div></div>
            <div className="serviceItem"><div className="serviceTitle">Підготовка аналітичних висновків та звітів</div></div>
          </div>
        </>
      ),
    },
    {
      id: 'about',
      title: 'Про мене',
      meta: '10+ років досвіду • OSINT • аналітика • розслідування',
      body: (
        <>
          <div className="mediaCard">
            <button
              type="button"
              className="mediaCard mediaCardBtn"
              onClick={() => openLb('/gallery/about.jpg', 'Олександр Шевченко')}
              aria-label="Відкрити фото про мене"
            >
              <div className="mediaFrame mediaFrame11" aria-label="Фото про мене">
                <img
                  className="mediaImg"
                  src="/gallery/about.jpg"
                  alt="Олександр Шевченко"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const phEl = e.currentTarget.parentElement?.querySelector('.mediaPlaceholder');
                    if (phEl) phEl.style.display = 'grid';
                  }}
                />
                <div className="mediaPlaceholder" style={{ display: 'none' }}>
                  <div className="placeholderText">Немає зображення</div>
                </div>
                <div className="tapHint" aria-hidden="true">Натисніть • повний екран</div>
              </div>
            </button>
          </div>

          <div className="legalBlock">
            <div className="serviceTitle">Олександр Шевченко</div>
            <p>
              Я маю понад 10 років практичного досвіду у сфері приватної детективної діяльності, проведення комплексних розслідувань та аналітичної роботи.
            </p>
            <p>
              Спеціалізуюсь на OSINT-дослідженнях, інформаційній аналітиці, комплексному аналізі ситуацій, корпоративних та приватних розслідуваннях, виявленні ризиків і прихованих зв’язків.
            </p>
            <p>
              У роботі використовую системний підхід, методи збору та аналізу інформації з відкритих джерел, перевірку фактів та побудову аналітичних висновків.
            </p>
            <p>
              Досвід включає проведення як приватних, так і корпоративних розслідувань, роботу зі складними інформаційними задачами та підготовку аналітичних матеріалів на професійному рівні.
            </p>
            <p>
              Основні принципи моєї роботи — конфіденційність, об’єктивність та увага до деталей.
            </p>
            <p>
              Моя задача — допомогти побачити реальну картину ситуації, відокремити факти від емоцій та знайти найбільш ефективне рішення.
            </p>
          </div>
        </>
      ),
    },
  ]), [openLb]);

  if (isMaintenanceActive) return <MaintenancePage />;

  return (
    <div className="page">
      <header className="hero">
        <div className="coverCol">
          <div className="coverFrame" aria-label="Аналітика та OSINT">
            <img
              className="coverImg"
              src={coverUrl}
              alt="Аналітика та OSINT"
              loading="eager"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const ph = e.currentTarget.parentElement?.querySelector('.coverPlaceholder');
                if (ph) ph.style.display = 'grid';
              }}
            />
            <div className="coverPlaceholder" style={{ display: 'none' }}>
              <div className="placeholderText">Немає зображення</div>
            </div>

            <div className="coverGlow" aria-hidden="true" />
            <div className="coverScrim" aria-hidden="true" />
          </div>

          <div className="miniBadges">
            <span className="badge">Аналітика</span>
            <span className="badgeStrong">OSINT</span>
            <span className="badge">Розслідування</span>
            <span className="badge">Траблшутинг</span>
          </div>
        </div>

        <div className="infoCol">
          <h1 className="title">Олександр Шевченко</h1>

          <p className="subtitle">
            Аналітика, OSINT, розслідування та траблшутинг для складних ситуацій, де потрібен системний і незалежний погляд.
          </p>

          <div className="textBlock heroTextBlock">
            <p>
              Допомагаю побачити реальну картину ситуації, відокремити факти від емоцій та знайти найбільш ефективне рішення.
            </p>
            <p>
              Працюю виключно в межах законодавства та з відкритими джерелами інформації.
            </p>
          </div>

          <div className="ctaCard" id="collab">
            <div className="ctaTitle">Звернення</div>
            <div className="ctaText">
              Опишіть ситуацію, ключові обставини та бажаний результат. Після цього можна визначити формат аналізу, строки та обсяг роботи.
            </div>

            <div className="ctaButtons ctaButtonsTwo">
              <a
                className="btn btnPrimary"
                href={emailUrl}
                onClick={onEmailClick}
              >
                <IconMail className="btnIcon" />
                <span className="btnLabel">Email</span>
              </a>

              <a
                className="btn btnGhost"
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                onClick={onWhatsAppClick}
              >
                <IconWhatsApp className="btnIcon" />
                <span className="btnLabel">WhatsApp</span>
              </a>
            </div>

            <form className="leadForm" onSubmit={submitLead}>
              <div className="leadFormTitle">Залишити запит</div>

              <label className="field">
                <span>Ім’я</span>
                <input
                  name="fullName"
                  type="text"
                  value={leadForm.fullName}
                  onChange={onLeadChange}
                  placeholder="Ваше ім’я"
                  autoComplete="name"
                />
              </label>

              <label className="field">
                <span>Email</span>
                <input
                  name="email"
                  type="email"
                  value={leadForm.email}
                  onChange={onLeadChange}
                  placeholder="name@example.com"
                  autoComplete="email"
                />
              </label>

              <label className="field">
                <span>Телефон</span>
                <input
                  name="phone"
                  type="tel"
                  value={leadForm.phone}
                  onChange={onLeadChange}
                  placeholder="+380 ..."
                  autoComplete="tel"
                />
              </label>

              <button
                type="submit"
                className="btn btnPrimary leadSubmit"
                disabled={leadStatus === 'loading'}
              >
                {leadStatus === 'loading' ? 'Надсилання...' : 'Надіслати запит'}
              </button>

              {leadMessage ? (
                <div className={`leadMessage ${leadStatus === 'success' ? 'leadMessageSuccess' : 'leadMessageError'}`}>
                  {leadMessage}
                </div>
              ) : null}
            </form>
          </div>
        </div>
      </header>

      <main className="main" id="services">
        <div className="sectionHead">
          <h2 className="sectionTitle">Інформація</h2>
          <div className="sectionHint">Секції згорнуті за замовчуванням — відкривайте по черзі.</div>
        </div>

        <div className="list">
          {sections.map((s) => {
            const isOpen = openIds.has(s.id);

            return (
              <section key={s.id} className="card">
                <button
                  type="button"
                  className="cardHeader"
                  onClick={() => toggle(s.id)}
                  aria-expanded={isOpen ? 'true' : 'false'}
                >
                  <div className="cardHeaderLeft">
                    <div className="cardTitleRow">
                      <div className="cardTitle">{s.title}</div>
                      <div className="cardBadge">{s.meta}</div>
                    </div>
                    <div className="cardMeta">Натисніть, щоб розгорнути</div>
                  </div>

                  <span className={`pmWrap ${isOpen ? 'pmOpen' : ''}`} aria-hidden="true">
                    <span className="pm" />
                  </span>
                </button>

                <div className={`cardBody ${isOpen ? 'cardBodyOpen' : ''}`}>
                  <div className="cardInner">
                    {s.body}
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        <footer className="footerCta">
          <div className="ctaTitle">Потрібен незалежний аналітичний погляд?</div>
          <div className="ctaText">
            Напишіть коротко, що сталося, які факти вже відомі та який результат потрібно отримати.
          </div>

          <div className="ctaButtons ctaButtonsTwo">
            <a
              className="btn btnPrimary"
              href={emailUrl}
              onClick={onEmailClick}
            >
              <IconMail className="btnIcon" />
              <span className="btnLabel">Email</span>
            </a>

            <a
              className="btn btnGhost"
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              onClick={onWhatsAppClick}
            >
              <IconWhatsApp className="btnIcon" />
              <span className="btnLabel">WhatsApp</span>
            </a>
          </div>
        </footer>
      </main>

      <div
        className={`lb ${lbOpen ? 'lbOpen' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Перегляд фото"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeLb();
        }}
        onTouchStart={(e) => {
          if (e.target === e.currentTarget) closeLb();
        }}
      >
        <div className="lbInner">
          <div className="lbFrame">
            <button type="button" className="lbClose" onClick={closeLb} aria-label="Закрити">
              <svg className="lbCloseIcon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6.2 6.2 17.8 17.8M17.8 6.2 6.2 17.8" />
              </svg>
            </button>

            {lightboxSrc ? (
              <img
                className="lbImg"
                src={lightboxSrc}
                alt={lightboxAlt || 'Фото'}
                draggable="false"
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function IconMail({ className = '' }){
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 6.5A2.5 2.5 0 0 1 5.5 4h13A2.5 2.5 0 0 1 21 6.5v11A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5v-11Zm2 .2v.1l6.5 4.9c.3.2.7.2 1 0L19 6.8v-.1a.5.5 0 0 0-.5-.5h-13a.5.5 0 0 0-.5.5Zm14 2.6-5.3 4c-1 .8-2.4.8-3.4 0L5 9.3v8.2c0 .3.2.5.5.5h13c.3 0 .5-.2.5-.5V9.3Z" />
    </svg>
  );
}

function IconWhatsApp({ className = '' }){
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a9.7 9.7 0 0 0-8.4 14.6L2.6 22l5.6-1.5A9.7 9.7 0 1 0 12 2Zm0 17.7c-1.5 0-3-.4-4.3-1.2l-.3-.2-3.3.9.9-3.2-.2-.3A7.7 7.7 0 1 1 12 19.7Z"/>
      <path d="M16.6 13.7c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.2l-.8 1c-.2.3-.4.3-.7.1-1.7-.8-2.8-2.1-3.2-2.8-.1-.3 0-.5.1-.6l.6-.7c.2-.2.2-.4.3-.6 0-.2 0-.4-.1-.6l-.9-2.1c-.2-.5-.5-.4-.7-.4h-.6c-.2 0-.6.1-.9.4-.3.3-1.2 1.2-1.2 2.9 0 1.7 1.2 3.4 1.4 3.6.2.2 2.4 3.6 5.8 5 .8.3 1.5.5 2 .6.8.3 1.6.2 2.2.1.7-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.1-1.4 0-.1-.2-.2-.5-.3Z"/>
    </svg>
  );
}

